import React, { useState } from 'react';
import { ExternalLink, Check, Clock, Plug, ChevronRight, Sparkles, X, Eye, EyeOff, Save, AlertTriangle, Loader2 } from 'lucide-react';
import { useAlert } from '../../../../context/AlertContext';

const STATUS = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    COMING_SOON: 'coming_soon'
};

const statusConfig = {
    [STATUS.ACTIVE]: { label: 'Aktif', color: '#10b981', bg: '#10b98115' },
    [STATUS.INACTIVE]: { label: 'Pasif', color: 'var(--text-muted)', bg: 'var(--bg-main)' },
    [STATUS.COMING_SOON]: { label: 'Yakında', color: '#f59e0b', bg: '#f59e0b15' }
};

const INTEGRATION_DEFS = [
    {
        id: 'iyzico',
        name: 'iyzico',
        description: 'Müşterileriniz QR menü üzerinden sipariş verirken iyzico ile güvenli ödeme alsın. Kendi iyzico hesabınızı bağlayarak portaldan direkt tahsilat yapın.',
        icon: '💳',
        iconBg: 'linear-gradient(135deg, #1a1f71, #2557d6)',
        iconColor: '#fff',
        tags: ['Ödeme', 'Portal'],
        configurable: true
    },
    {
        id: 'paytr',
        name: 'PayTR',
        description: 'PayTR altyapısı ile müşterilerinizden QR menü portalı üzerinden güvenli kredi kartı / banka kartı ödemesi alın.',
        icon: '💰',
        iconBg: 'linear-gradient(135deg, #0055ff, #00b4d8)',
        iconColor: '#fff',
        tags: ['Ödeme', 'Portal'],
        configurable: false,
        comingSoon: true
    },
    {
        id: 'param',
        name: 'Param (TurkPOS)',
        description: 'Param / TurkPOS altyapısı ile müşterilerinizden QR menü portalı üzerinden güvenli kredi kartı ödemesi alın.',
        icon: '💎',
        iconBg: 'linear-gradient(135deg, #6b21a8, #c026d3)',
        iconColor: '#fff',
        tags: ['Ödeme', 'Portal'],
        configurable: false,
        comingSoon: true
    },
    {
        id: 'whatsapp',
        name: 'WhatsApp Business',
        description: 'Müşterilerinize kampanya bildirimleri, sadakat hatırlatmaları ve hoş geldin mesajları gönderin.',
        iconSvg: (
            <svg viewBox="0 0 24 24" width="28" height="28" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
        ),
        iconBg: '#25D36615',
        tags: ['Mesajlaşma', 'Kampanya'],
        status: STATUS.COMING_SOON
    },
    {
        id: 'instagram',
        name: 'Instagram DM',
        description: 'Instagram sayfanıza gelen direkt mesajları Dashboard üzerinden okuyun ve cevaplayın.',
        iconSvg: (
            <svg viewBox="0 0 24 24" width="26" height="26">
                <defs>
                    <linearGradient id="ig-grad" x1="0%" y1="100%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#feda75" />
                        <stop offset="25%" stopColor="#fa7e1e" />
                        <stop offset="50%" stopColor="#d62976" />
                        <stop offset="75%" stopColor="#962fbf" />
                        <stop offset="100%" stopColor="#4f5bd5" />
                    </linearGradient>
                </defs>
                <rect x="2" y="2" width="20" height="20" rx="5" fill="none" stroke="url(#ig-grad)" strokeWidth="2" />
                <circle cx="12" cy="12" r="5" fill="none" stroke="url(#ig-grad)" strokeWidth="2" />
                <circle cx="17.5" cy="6.5" r="1.5" fill="url(#ig-grad)" />
            </svg>
        ),
        iconBg: '#d6297610',
        tags: ['Sosyal Medya', 'Mesajlaşma'],
        status: STATUS.COMING_SOON
    },
    {
        id: 'yemeksepeti',
        name: 'Yemeksepeti',
        description: 'Yemeksepeti siparişlerinizi ve menü bilgilerinizi otomatik olarak senkronize edin.',
        iconSvg: <span style={{ fontSize: '26px', fontWeight: 900, color: '#fa0050' }}>Y</span>,
        iconBg: '#fa005010',
        tags: ['Sipariş', 'Menü'],
        status: STATUS.COMING_SOON
    },
    {
        id: 'getir',
        name: 'Getir Yemek',
        description: 'Getir Yemek siparişlerinizi dashboard üzerinden yönetin ve menünüzü senkronize edin.',
        iconSvg: <span style={{ fontSize: '26px', fontWeight: 900, color: '#5d3ebc' }}>G</span>,
        iconBg: '#5d3ebc10',
        tags: ['Sipariş', 'Menü'],
        status: STATUS.COMING_SOON
    },
    {
        id: 'trendyol',
        name: 'Trendyol Yemek',
        description: 'Trendyol Yemek siparişlerinizi ve menünüzü otomatik olarak senkronize edin.',
        iconSvg: <span style={{ fontSize: '26px', fontWeight: 900, color: '#f27a1a' }}>T</span>,
        iconBg: '#f27a1a10',
        tags: ['Sipariş', 'Menü'],
        status: STATUS.COMING_SOON
    },
    {
        id: 'migros',
        name: 'Migros Yemek',
        description: 'Migros Yemek platformundaki siparişlerinizi ve menünüzü dashboard üzerinden yönetin.',
        iconSvg: <span style={{ fontSize: '26px', fontWeight: 900, color: '#ff6600' }}>M</span>,
        iconBg: '#ff660010',
        tags: ['Sipariş', 'Menü'],
        status: STATUS.COMING_SOON
    },
    {
        id: 'google-calendar',
        name: 'Google Takvim',
        description: 'Personel vardiya planlaması ve rezervasyonları Google Takvim ile otomatik senkronize edin.',
        iconSvg: (
            <svg viewBox="0 0 24 24" width="26" height="26">
                <path fill="#4285F4" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12s4.477 10 10 10 10-4.477 10-10z" opacity="0.1" />
                <path fill="#4285F4" d="M12 7v5l4.25 2.52.77-1.28-3.52-2.09V7z" />
                <path fill="#EA4335" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z" />
            </svg>
        ),
        iconBg: '#4285F415',
        tags: ['Takvim', 'Planlama'],
        status: STATUS.COMING_SOON
    }
];

// --- iyzico Config Modal ---
const IyzicoConfigModal = ({ isOpen, onClose, currentConfig, onSave, isSaving }) => {
    const [form, setForm] = useState({
        apiKey: '',
        secretKey: '',
        baseUrl: 'https://api.iyzipay.com',
        ...currentConfig
    });
    const [showSecret, setShowSecret] = useState(false);
    const [testResult, setTestResult] = useState(null);

    React.useEffect(() => {
        if (isOpen) {
            setForm({
                apiKey: '',
                secretKey: '',
                baseUrl: 'https://api.iyzipay.com',
                ...currentConfig
            });
            setShowSecret(false);
            setTestResult(null);
        }
    }, [isOpen, currentConfig]);

    if (!isOpen) return null;

    const isSandbox = form.baseUrl?.includes('sandbox');

    const handleSave = () => {
        onSave({
            apiKey: form.apiKey.trim(),
            secretKey: form.secretKey.trim(),
            baseUrl: form.baseUrl,
            enabled: !!(form.apiKey.trim() && form.secretKey.trim()),
            connectedAt: new Date().toISOString()
        });
    };

    const handleDisconnect = () => {
        onSave({
            apiKey: '',
            secretKey: '',
            baseUrl: 'https://api.iyzipay.com',
            enabled: false,
            connectedAt: null
        });
    };

    const isConnected = currentConfig?.enabled && currentConfig?.apiKey;

    const maskKey = (key) => {
        if (!key) return '';
        if (key.length <= 8) return '••••••••';
        return key.slice(0, 4) + '••••••••' + key.slice(-4);
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: '1rem'
        }} onClick={onClose}>
            <div style={{
                background: 'var(--bg-card)', borderRadius: '24px',
                width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto',
                boxShadow: '0 25px 60px rgba(0,0,0,0.15)'
            }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={{
                    padding: '2rem 2rem 1.5rem', borderBottom: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '44px', height: '44px', borderRadius: '14px',
                            background: 'linear-gradient(135deg, #1a1f71, #2557d6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '20px'
                        }}>💳</div>
                        <div>
                            <h3 style={{ margin: 0, fontWeight: 800, fontSize: '1.125rem' }}>iyzico Entegrasyonu</h3>
                            <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                                Müşteri portalı ödeme altyapısı
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{
                        background: 'var(--bg-main)', border: 'none', borderRadius: '10px',
                        width: '36px', height: '36px', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)'
                    }}>
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: '2rem' }}>
                    {/* Status Banner */}
                    {isConnected && (
                        <div style={{
                            background: '#10b98115', border: '1px solid #10b98130',
                            borderRadius: '14px', padding: '1rem 1.25rem',
                            display: 'flex', alignItems: 'center', gap: '10px',
                            marginBottom: '1.5rem'
                        }}>
                            <Check size={18} style={{ color: '#10b981' }} />
                            <div>
                                <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#10b981' }}>Bağlantı Aktif</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                    {currentConfig.connectedAt && `Bağlanma: ${new Date(currentConfig.connectedAt).toLocaleDateString('tr-TR')}`}
                                    {isSandbox && ' · Sandbox modu'}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Info */}
                    <div style={{
                        background: 'rgba(var(--primary-rgb), 0.05)', borderRadius: '14px',
                        padding: '1rem 1.25rem', marginBottom: '1.5rem',
                        fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.7
                    }}>
                        iyzico hesabınızı bağladığınızda, müşterileriniz QR menü portalından sipariş verirken kredi kartı / banka kartı ile ödeme yapabilecektir. Ödemeler doğrudan sizin iyzico hesabınıza geçer.
                    </div>

                    {/* Environment Toggle */}
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ fontSize: '0.8125rem', fontWeight: 700, display: 'block', marginBottom: '8px' }}>
                            Ortam
                        </label>
                        <div style={{
                            display: 'flex', background: 'var(--bg-main)', borderRadius: '12px',
                            padding: '4px', border: '1px solid var(--border)'
                        }}>
                            <button
                                type="button"
                                onClick={() => setForm(f => ({ ...f, baseUrl: 'https://api.iyzipay.com' }))}
                                style={{
                                    flex: 1, padding: '10px', borderRadius: '10px', border: 'none',
                                    fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer',
                                    background: !isSandbox ? 'var(--bg-card)' : 'transparent',
                                    color: !isSandbox ? 'var(--text-main)' : 'var(--text-muted)',
                                    boxShadow: !isSandbox ? '0 2px 8px rgba(0,0,0,0.05)' : 'none',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Canlı (Production)
                            </button>
                            <button
                                type="button"
                                onClick={() => setForm(f => ({ ...f, baseUrl: 'https://sandbox-api.iyzipay.com' }))}
                                style={{
                                    flex: 1, padding: '10px', borderRadius: '10px', border: 'none',
                                    fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer',
                                    background: isSandbox ? 'var(--bg-card)' : 'transparent',
                                    color: isSandbox ? '#f59e0b' : 'var(--text-muted)',
                                    boxShadow: isSandbox ? '0 2px 8px rgba(0,0,0,0.05)' : 'none',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Sandbox (Test)
                            </button>
                        </div>
                        {isSandbox && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', fontSize: '0.75rem', color: '#f59e0b' }}>
                                <AlertTriangle size={12} /> Test modunda gerçek ödeme alınmaz
                            </div>
                        )}
                    </div>

                    {/* API Key */}
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ fontSize: '0.8125rem', fontWeight: 700, display: 'block', marginBottom: '8px' }}>
                            API Key
                        </label>
                        <input
                            type="text"
                            value={form.apiKey}
                            onChange={e => setForm(f => ({ ...f, apiKey: e.target.value }))}
                            placeholder={isConnected ? maskKey(currentConfig.apiKey) : 'iyzico API anahtarınız'}
                            style={{
                                width: '100%', padding: '12px 16px', borderRadius: '12px',
                                border: '1px solid var(--border)', background: 'var(--bg-main)',
                                color: 'var(--text-main)', fontSize: '0.875rem', fontFamily: 'monospace',
                                outline: 'none', boxSizing: 'border-box',
                                transition: 'border-color 0.2s'
                            }}
                            onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                            onBlur={e => e.target.style.borderColor = 'var(--border)'}
                        />
                    </div>

                    {/* Secret Key */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ fontSize: '0.8125rem', fontWeight: 700, display: 'block', marginBottom: '8px' }}>
                            Secret Key
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showSecret ? 'text' : 'password'}
                                value={form.secretKey}
                                onChange={e => setForm(f => ({ ...f, secretKey: e.target.value }))}
                                placeholder={isConnected ? maskKey(currentConfig.secretKey) : 'iyzico gizli anahtarınız'}
                                style={{
                                    width: '100%', padding: '12px 48px 12px 16px', borderRadius: '12px',
                                    border: '1px solid var(--border)', background: 'var(--bg-main)',
                                    color: 'var(--text-main)', fontSize: '0.875rem', fontFamily: 'monospace',
                                    outline: 'none', boxSizing: 'border-box',
                                    transition: 'border-color 0.2s'
                                }}
                                onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                                onBlur={e => e.target.style.borderColor = 'var(--border)'}
                            />
                            <button
                                type="button"
                                onClick={() => setShowSecret(!showSecret)}
                                style={{
                                    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: 'var(--text-muted)', display: 'flex'
                                }}
                            >
                                {showSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* Test result */}
                    {testResult && (
                        <div style={{
                            background: testResult.success ? '#10b98115' : '#ef444415',
                            border: `1px solid ${testResult.success ? '#10b98130' : '#ef444430'}`,
                            borderRadius: '12px', padding: '0.75rem 1rem', marginBottom: '1.5rem',
                            fontSize: '0.8125rem', fontWeight: 600,
                            color: testResult.success ? '#10b981' : '#ef4444'
                        }}>
                            {testResult.message}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '1.5rem 2rem', borderTop: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px'
                }}>
                    {isConnected ? (
                        <button
                            onClick={handleDisconnect}
                            disabled={isSaving}
                            style={{
                                padding: '10px 20px', borderRadius: '12px', border: '1px solid #ef444430',
                                background: '#ef444410', color: '#ef4444', fontWeight: 700,
                                fontSize: '0.8125rem', cursor: 'pointer'
                            }}
                        >
                            Bağlantıyı Kes
                        </button>
                    ) : (
                        <div />
                    )}
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={onClose}
                            style={{
                                padding: '10px 20px', borderRadius: '12px',
                                border: '1px solid var(--border)', background: 'var(--bg-main)',
                                color: 'var(--text-main)', fontWeight: 700, fontSize: '0.8125rem',
                                cursor: 'pointer'
                            }}
                        >
                            İptal
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving || (!form.apiKey.trim() && !isConnected)}
                            style={{
                                padding: '10px 24px', borderRadius: '12px', border: 'none',
                                background: 'var(--primary)', color: 'white', fontWeight: 700,
                                fontSize: '0.8125rem', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '8px',
                                opacity: (isSaving || (!form.apiKey.trim() && !isConnected)) ? 0.5 : 1
                            }}
                        >
                            {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                            {isConnected ? 'Güncelle' : 'Bağlan'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- PayTR Config Modal ---
const PaytrConfigModal = ({ isOpen, onClose, currentConfig, onSave, isSaving }) => {
    const [form, setForm] = useState({
        merchantId: '',
        merchantKey: '',
        merchantSalt: '',
        testMode: false,
        ...currentConfig
    });
    const [showKey, setShowKey] = useState(false);
    const [showSalt, setShowSalt] = useState(false);

    React.useEffect(() => {
        if (isOpen) {
            setForm({
                merchantId: '',
                merchantKey: '',
                merchantSalt: '',
                testMode: false,
                ...currentConfig
            });
            setShowKey(false);
            setShowSalt(false);
        }
    }, [isOpen, currentConfig]);

    if (!isOpen) return null;

    const handleSave = () => {
        onSave({
            merchantId: form.merchantId.trim(),
            merchantKey: form.merchantKey.trim(),
            merchantSalt: form.merchantSalt.trim(),
            testMode: form.testMode,
            enabled: !!(form.merchantId.trim() && form.merchantKey.trim() && form.merchantSalt.trim()),
            connectedAt: new Date().toISOString()
        });
    };

    const handleDisconnect = () => {
        onSave({
            merchantId: '',
            merchantKey: '',
            merchantSalt: '',
            testMode: false,
            enabled: false,
            connectedAt: null
        });
    };

    const isConnected = currentConfig?.enabled && currentConfig?.merchantId;

    const maskKey = (key) => {
        if (!key) return '';
        if (key.length <= 8) return '••••••••';
        return key.slice(0, 4) + '••••••••' + key.slice(-4);
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: '1rem'
        }} onClick={onClose}>
            <div style={{
                background: 'var(--bg-card)', borderRadius: '24px',
                width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto',
                boxShadow: '0 25px 60px rgba(0,0,0,0.15)'
            }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={{
                    padding: '2rem 2rem 1.5rem', borderBottom: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '44px', height: '44px', borderRadius: '14px',
                            background: 'linear-gradient(135deg, #0055ff, #00b4d8)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '20px'
                        }}>💰</div>
                        <div>
                            <h3 style={{ margin: 0, fontWeight: 800, fontSize: '1.125rem' }}>PayTR Entegrasyonu</h3>
                            <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                                Müşteri portalı ödeme altyapısı
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{
                        background: 'var(--bg-main)', border: 'none', borderRadius: '10px',
                        width: '36px', height: '36px', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)'
                    }}>
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: '2rem' }}>
                    {/* Status Banner */}
                    {isConnected && (
                        <div style={{
                            background: '#10b98115', border: '1px solid #10b98130',
                            borderRadius: '14px', padding: '1rem 1.25rem',
                            display: 'flex', alignItems: 'center', gap: '10px',
                            marginBottom: '1.5rem'
                        }}>
                            <Check size={18} style={{ color: '#10b981' }} />
                            <div>
                                <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#10b981' }}>Bağlantı Aktif</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                    {currentConfig.connectedAt && `Bağlanma: ${new Date(currentConfig.connectedAt).toLocaleDateString('tr-TR')}`}
                                    {currentConfig.testMode && ' · Test modu'}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Info */}
                    <div style={{
                        background: 'rgba(var(--primary-rgb), 0.05)', borderRadius: '14px',
                        padding: '1rem 1.25rem', marginBottom: '1.5rem',
                        fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.7
                    }}>
                        PayTR hesabınızı bağladığınızda, müşterileriniz QR menü portalından sipariş verirken kredi kartı / banka kartı ile ödeme yapabilecektir. Ödemeler doğrudan sizin PayTR hesabınıza geçer.
                    </div>

                    {/* Test Mode Toggle */}
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ fontSize: '0.8125rem', fontWeight: 700, display: 'block', marginBottom: '8px' }}>
                            Ortam
                        </label>
                        <div style={{
                            display: 'flex', background: 'var(--bg-main)', borderRadius: '12px',
                            padding: '4px', border: '1px solid var(--border)'
                        }}>
                            <button
                                type="button"
                                onClick={() => setForm(f => ({ ...f, testMode: false }))}
                                style={{
                                    flex: 1, padding: '10px', borderRadius: '10px', border: 'none',
                                    fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer',
                                    background: !form.testMode ? 'var(--bg-card)' : 'transparent',
                                    color: !form.testMode ? 'var(--text-main)' : 'var(--text-muted)',
                                    boxShadow: !form.testMode ? '0 2px 8px rgba(0,0,0,0.05)' : 'none',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Canlı (Production)
                            </button>
                            <button
                                type="button"
                                onClick={() => setForm(f => ({ ...f, testMode: true }))}
                                style={{
                                    flex: 1, padding: '10px', borderRadius: '10px', border: 'none',
                                    fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer',
                                    background: form.testMode ? 'var(--bg-card)' : 'transparent',
                                    color: form.testMode ? '#f59e0b' : 'var(--text-muted)',
                                    boxShadow: form.testMode ? '0 2px 8px rgba(0,0,0,0.05)' : 'none',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Test Modu
                            </button>
                        </div>
                        {form.testMode && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', fontSize: '0.75rem', color: '#f59e0b' }}>
                                <AlertTriangle size={12} /> Test modunda gerçek ödeme alınmaz
                            </div>
                        )}
                    </div>

                    {/* Merchant ID */}
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ fontSize: '0.8125rem', fontWeight: 700, display: 'block', marginBottom: '8px' }}>
                            Merchant ID (Mağaza No)
                        </label>
                        <input
                            type="text"
                            value={form.merchantId}
                            onChange={e => setForm(f => ({ ...f, merchantId: e.target.value }))}
                            placeholder={isConnected ? maskKey(currentConfig.merchantId) : 'PayTR Mağaza Numaranız'}
                            style={{
                                width: '100%', padding: '12px 16px', borderRadius: '12px',
                                border: '1px solid var(--border)', background: 'var(--bg-main)',
                                color: 'var(--text-main)', fontSize: '0.875rem', fontFamily: 'monospace',
                                outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s'
                            }}
                            onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                            onBlur={e => e.target.style.borderColor = 'var(--border)'}
                        />
                    </div>

                    {/* Merchant Key */}
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ fontSize: '0.8125rem', fontWeight: 700, display: 'block', marginBottom: '8px' }}>
                            Merchant Key
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showKey ? 'text' : 'password'}
                                value={form.merchantKey}
                                onChange={e => setForm(f => ({ ...f, merchantKey: e.target.value }))}
                                placeholder={isConnected ? maskKey(currentConfig.merchantKey) : 'PayTR Merchant Key'}
                                style={{
                                    width: '100%', padding: '12px 48px 12px 16px', borderRadius: '12px',
                                    border: '1px solid var(--border)', background: 'var(--bg-main)',
                                    color: 'var(--text-main)', fontSize: '0.875rem', fontFamily: 'monospace',
                                    outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s'
                                }}
                                onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                                onBlur={e => e.target.style.borderColor = 'var(--border)'}
                            />
                            <button
                                type="button"
                                onClick={() => setShowKey(!showKey)}
                                style={{
                                    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: 'var(--text-muted)', display: 'flex'
                                }}
                            >
                                {showKey ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>

                    {/* Merchant Salt */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ fontSize: '0.8125rem', fontWeight: 700, display: 'block', marginBottom: '8px' }}>
                            Merchant Salt
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showSalt ? 'text' : 'password'}
                                value={form.merchantSalt}
                                onChange={e => setForm(f => ({ ...f, merchantSalt: e.target.value }))}
                                placeholder={isConnected ? maskKey(currentConfig.merchantSalt) : 'PayTR Merchant Salt'}
                                style={{
                                    width: '100%', padding: '12px 48px 12px 16px', borderRadius: '12px',
                                    border: '1px solid var(--border)', background: 'var(--bg-main)',
                                    color: 'var(--text-main)', fontSize: '0.875rem', fontFamily: 'monospace',
                                    outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s'
                                }}
                                onFocus={e => e.target.style.borderColor = 'var(--primary)'}
                                onBlur={e => e.target.style.borderColor = 'var(--border)'}
                            />
                            <button
                                type="button"
                                onClick={() => setShowSalt(!showSalt)}
                                style={{
                                    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: 'var(--text-muted)', display: 'flex'
                                }}
                            >
                                {showSalt ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    padding: '1.5rem 2rem', borderTop: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px'
                }}>
                    {isConnected ? (
                        <button
                            onClick={handleDisconnect}
                            disabled={isSaving}
                            style={{
                                padding: '10px 20px', borderRadius: '12px', border: '1px solid #ef444430',
                                background: '#ef444410', color: '#ef4444', fontWeight: 700,
                                fontSize: '0.8125rem', cursor: 'pointer'
                            }}
                        >
                            Bağlantıyı Kes
                        </button>
                    ) : (
                        <div />
                    )}
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={onClose}
                            style={{
                                padding: '10px 20px', borderRadius: '12px',
                                border: '1px solid var(--border)', background: 'var(--bg-main)',
                                color: 'var(--text-main)', fontWeight: 700, fontSize: '0.8125rem',
                                cursor: 'pointer'
                            }}
                        >
                            İptal
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving || (!form.merchantId.trim() && !isConnected)}
                            style={{
                                padding: '10px 24px', borderRadius: '12px', border: 'none',
                                background: 'var(--primary)', color: 'white', fontWeight: 700,
                                fontSize: '0.8125rem', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '8px',
                                opacity: (isSaving || (!form.merchantId.trim() && !isConnected)) ? 0.5 : 1
                            }}
                        >
                            {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                            {isConnected ? 'Güncelle' : 'Bağlan'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Param Config Modal ---
const ParamConfigModal = ({ isOpen, onClose, currentConfig, onSave, isSaving }) => {
    const [form, setForm] = useState({
        clientCode: '',
        clientUsername: '',
        clientPassword: '',
        guid: '',
        testMode: false,
        ...currentConfig
    });
    const [showPassword, setShowPassword] = useState(false);

    React.useEffect(() => {
        if (isOpen) {
            setForm({
                clientCode: '',
                clientUsername: '',
                clientPassword: '',
                guid: '',
                testMode: false,
                ...currentConfig
            });
            setShowPassword(false);
        }
    }, [isOpen, currentConfig]);

    if (!isOpen) return null;

    const handleSave = () => {
        onSave({
            clientCode: form.clientCode.trim(),
            clientUsername: form.clientUsername.trim(),
            clientPassword: form.clientPassword.trim(),
            guid: form.guid.trim(),
            testMode: form.testMode,
            enabled: !!(form.clientCode.trim() && form.guid.trim()),
            connectedAt: new Date().toISOString()
        });
    };

    const handleDisconnect = () => {
        onSave({
            clientCode: '',
            clientUsername: '',
            clientPassword: '',
            guid: '',
            testMode: false,
            enabled: false,
            connectedAt: null
        });
    };

    const isConnected = currentConfig?.enabled && currentConfig?.clientCode;

    const maskKey = (key) => {
        if (!key) return '';
        if (key.length <= 8) return '••••••••';
        return key.slice(0, 4) + '••••••••' + key.slice(-4);
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: '1rem'
        }} onClick={onClose}>
            <div style={{
                background: 'var(--bg-card)', borderRadius: '24px',
                width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto',
                boxShadow: '0 25px 60px rgba(0,0,0,0.15)'
            }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={{
                    padding: '2rem 2rem 1.5rem', borderBottom: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '44px', height: '44px', borderRadius: '14px',
                            background: 'linear-gradient(135deg, #6b21a8, #c026d3)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '20px'
                        }}>💎</div>
                        <div>
                            <h3 style={{ margin: 0, fontWeight: 800, fontSize: '1.125rem' }}>Param Entegrasyonu</h3>
                            <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                                Müşteri portalı ödeme altyapısı (TurkPOS)
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{
                        background: 'var(--bg-main)', border: 'none', borderRadius: '10px',
                        width: '36px', height: '36px', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)'
                    }}>
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div style={{ padding: '2rem' }}>
                    {/* Status Banner */}
                    {isConnected && (
                        <div style={{
                            background: '#10b98115', border: '1px solid #10b98130',
                            borderRadius: '14px', padding: '1rem 1.25rem',
                            display: 'flex', alignItems: 'center', gap: '10px',
                            marginBottom: '1.5rem'
                        }}>
                            <Check size={18} style={{ color: '#10b981' }} />
                            <div>
                                <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#10b981' }}>Bağlantı Aktif</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                    {currentConfig.connectedAt && `Bağlanma: ${new Date(currentConfig.connectedAt).toLocaleDateString('tr-TR')}`}
                                    {currentConfig.testMode && ' · Test modu'}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Environment Toggle */}
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ fontSize: '0.8125rem', fontWeight: 700, display: 'block', marginBottom: '8px' }}>
                            Ortam
                        </label>
                        <div style={{
                            display: 'flex', background: 'var(--bg-main)', borderRadius: '12px',
                            padding: '4px', border: '1px solid var(--border)'
                        }}>
                            <button
                                type="button"
                                onClick={() => setForm(f => ({ ...f, testMode: false }))}
                                style={{
                                    flex: 1, padding: '10px', borderRadius: '10px', border: 'none',
                                    fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer',
                                    background: !form.testMode ? 'var(--bg-card)' : 'transparent',
                                    color: !form.testMode ? 'var(--text-main)' : 'var(--text-muted)',
                                    boxShadow: !form.testMode ? '0 2px 8px rgba(0,0,0,0.05)' : 'none',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Canlı (Production)
                            </button>
                            <button
                                type="button"
                                onClick={() => setForm(f => ({ ...f, testMode: true }))}
                                style={{
                                    flex: 1, padding: '10px', borderRadius: '10px', border: 'none',
                                    fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer',
                                    background: form.testMode ? 'var(--bg-card)' : 'transparent',
                                    color: form.testMode ? '#f59e0b' : 'var(--text-muted)',
                                    boxShadow: form.testMode ? '0 2px 8px rgba(0,0,0,0.05)' : 'none',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Test Modu
                            </button>
                        </div>
                        {form.testMode && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', fontSize: '0.75rem', color: '#f59e0b' }}>
                                <AlertTriangle size={12} /> Test modunda gerçek ödeme alınmaz
                            </div>
                        )}
                    </div>

                    {/* CLIENT_CODE */}
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ fontSize: '0.8125rem', fontWeight: 700, display: 'block', marginBottom: '8px' }}>
                            Client Code (Müşteri Kodu)
                        </label>
                        <input
                            type="text"
                            value={form.clientCode}
                            onChange={e => setForm(f => ({ ...f, clientCode: e.target.value }))}
                            placeholder={isConnected ? maskKey(currentConfig.clientCode) : 'Param Müşteri Kodunuz'}
                            style={{
                                width: '100%', padding: '12px 16px', borderRadius: '12px',
                                border: '1px solid var(--border)', background: 'var(--bg-main)',
                                color: 'var(--text-main)', fontSize: '0.875rem', fontFamily: 'monospace',
                                outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s'
                            }}
                        />
                    </div>

                    {/* GUID */}
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ fontSize: '0.8125rem', fontWeight: 700, display: 'block', marginBottom: '8px' }}>
                            GUID (İşyeri Anahtarı)
                        </label>
                        <input
                            type="text"
                            value={form.guid}
                            onChange={e => setForm(f => ({ ...f, guid: e.target.value }))}
                            placeholder={isConnected ? maskKey(currentConfig.guid) : 'Param GUID Değeriniz'}
                            style={{
                                width: '100%', padding: '12px 16px', borderRadius: '12px',
                                border: '1px solid var(--border)', background: 'var(--bg-main)',
                                color: 'var(--text-main)', fontSize: '0.875rem', fontFamily: 'monospace',
                                outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s'
                            }}
                        />
                    </div>

                    {/* CLIENT_USERNAME */}
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{ fontSize: '0.8125rem', fontWeight: 700, display: 'block', marginBottom: '8px' }}>
                            API Kullanıcı Adı (Opsiyonel)
                        </label>
                        <input
                            type="text"
                            value={form.clientUsername}
                            onChange={e => setForm(f => ({ ...f, clientUsername: e.target.value }))}
                            placeholder={'Param API Kullanıcı Adı'}
                            style={{
                                width: '100%', padding: '12px 16px', borderRadius: '12px',
                                border: '1px solid var(--border)', background: 'var(--bg-main)',
                                color: 'var(--text-main)', fontSize: '0.875rem', outline: 'none',
                                boxSizing: 'border-box', transition: 'border-color 0.2s'
                            }}
                        />
                    </div>

                    {/* CLIENT_PASSWORD */}
                    <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ fontSize: '0.8125rem', fontWeight: 700, display: 'block', marginBottom: '8px' }}>
                            API Parolası (Opsiyonel)
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={form.clientPassword}
                                onChange={e => setForm(f => ({ ...f, clientPassword: e.target.value }))}
                                placeholder={'Param API Parolanız'}
                                style={{
                                    width: '100%', padding: '12px 48px 12px 16px', borderRadius: '12px',
                                    border: '1px solid var(--border)', background: 'var(--bg-main)',
                                    color: 'var(--text-main)', fontSize: '0.875rem', outline: 'none',
                                    boxSizing: 'border-box', transition: 'border-color 0.2s'
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: 'var(--text-muted)', display: 'flex'
                                }}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={{
                    padding: '1.5rem 2rem', borderTop: '1px solid var(--border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px'
                }}>
                    {isConnected ? (
                        <button
                            onClick={handleDisconnect}
                            disabled={isSaving}
                            style={{
                                padding: '10px 20px', borderRadius: '12px', border: '1px solid #ef444430',
                                background: '#ef444410', color: '#ef4444', fontWeight: 700,
                                fontSize: '0.8125rem', cursor: 'pointer'
                            }}
                        >
                            Bağlantıyı Kes
                        </button>
                    ) : (
                        <div />
                    )}
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={onClose}
                            style={{
                                padding: '10px 20px', borderRadius: '12px',
                                border: '1px solid var(--border)', background: 'var(--bg-main)',
                                color: 'var(--text-main)', fontWeight: 700, fontSize: '0.8125rem',
                                cursor: 'pointer'
                            }}
                        >
                            İptal
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving || (!form.clientCode.trim() && !isConnected)}
                            style={{
                                padding: '10px 24px', borderRadius: '12px', border: 'none',
                                background: 'var(--primary)', color: 'white', fontWeight: 700,
                                fontSize: '0.8125rem', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '8px',
                                opacity: (isSaving || (!form.clientCode.trim() && !isConnected)) ? 0.5 : 1
                            }}
                        >
                            {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                            {isConnected ? 'Güncelle' : 'Bağlan'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};


// --- Integration Card ---
const IntegrationCard = ({ integration, status, onAction }) => {
    const [hovered, setHovered] = useState(false);
    const statusInfo = statusConfig[status];
    const isActive = status === STATUS.ACTIVE;

    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                background: 'var(--bg-card)',
                border: isActive
                    ? '2px solid var(--primary)'
                    : `1px solid ${hovered ? 'var(--primary)' : 'var(--border)'}`,
                borderRadius: '20px',
                padding: '1.75rem',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                transition: 'all 0.25s ease',
                transform: hovered ? 'translateY(-2px)' : 'none',
                boxShadow: hovered
                    ? '0 12px 32px rgba(0,0,0,0.08)'
                    : '0 2px 8px rgba(0,0,0,0.03)',
                cursor: status === STATUS.COMING_SOON ? 'default' : 'pointer',
                opacity: status === STATUS.COMING_SOON ? 0.75 : 1
            }}
            onClick={() => {
                if (integration.configurable) onAction(integration);
            }}
        >
            {/* Active Badge */}
            {isActive && (
                <div style={{
                    position: 'absolute', top: '-10px', right: '16px',
                    background: 'var(--primary)', color: 'white',
                    padding: '3px 12px', borderRadius: '20px',
                    fontSize: '0.625rem', fontWeight: 800, textTransform: 'uppercase',
                    letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: '4px'
                }}>
                    <Sparkles size={10} /> Aktif
                </div>
            )}

            {/* Status Badge */}
            <div style={{
                position: 'absolute', top: '16px', right: '16px',
                display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: '0.6875rem', fontWeight: 700, color: statusInfo.color
            }}>
                <div style={{
                    width: '6px', height: '6px', borderRadius: '50%',
                    background: statusInfo.color
                }} />
                {statusInfo.label}
            </div>

            {/* Icon */}
            <div style={{
                width: '52px', height: '52px', borderRadius: '16px',
                background: integration.iconBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: '1.25rem',
                fontSize: integration.icon ? '24px' : undefined,
                color: integration.iconColor
            }}>
                {integration.icon ? integration.icon : integration.iconSvg}
            </div>

            {/* Name */}
            <h3 style={{
                fontSize: '1.0625rem', fontWeight: 800,
                margin: '0 0 0.5rem 0', color: 'var(--text-main)'
            }}>
                {integration.name}
            </h3>

            {/* Description */}
            <p style={{
                fontSize: '0.8125rem', color: 'var(--text-muted)',
                lineHeight: 1.6, margin: '0 0 1.5rem 0', flex: 1, fontWeight: 500
            }}>
                {integration.description}
            </p>

            {/* Tags + Action */}
            <div style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', marginTop: 'auto'
            }}>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {integration.tags.map(tag => (
                        <span key={tag} style={{
                            background: 'var(--bg-main)', color: 'var(--text-muted)',
                            padding: '4px 10px', borderRadius: '8px',
                            fontSize: '0.6875rem', fontWeight: 700
                        }}>
                            {tag}
                        </span>
                    ))}
                </div>

                {isActive ? (
                    <button
                        onClick={e => { e.stopPropagation(); onAction(integration); }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)',
                            border: 'none', padding: '8px 16px', borderRadius: '10px',
                            fontSize: '0.8125rem', fontWeight: 700, cursor: 'pointer'
                        }}
                    >
                        <Plug size={14} /> Yönet
                    </button>
                ) : status === STATUS.COMING_SOON ? (
                    <span style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        color: '#f59e0b', fontSize: '0.8125rem', fontWeight: 700
                    }}>
                        <Clock size={14} /> Yakında
                    </span>
                ) : (
                    <button
                        onClick={e => { e.stopPropagation(); onAction(integration); }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '6px',
                            background: 'var(--bg-main)', color: 'var(--text-main)',
                            border: '1px solid var(--border)', padding: '8px 16px',
                            borderRadius: '10px', fontSize: '0.8125rem', fontWeight: 700,
                            cursor: 'pointer'
                        }}
                    >
                        <Plug size={14} /> Bağla
                    </button>
                )}
            </div>
        </div>
    );
};

// --- Main Component ---
const Integrations = ({ tenantData, effectiveTenantId, handleUpdateField }) => {
    const { showAlert } = useAlert();
    const [iyzicoModalOpen, setIyzicoModalOpen] = useState(false);
    const [paytrModalOpen, setPaytrModalOpen] = useState(false);
    const [paramModalOpen, setParamModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const savedIntegrations = tenantData?.integrations || {};
    const iyzicoConfig = savedIntegrations.iyzico || {};
    const paytrConfig = savedIntegrations.paytr || {};
    const paramConfig = savedIntegrations.param || {};

    const getStatus = (def) => {
        if (def.status) return def.status; // hardcoded status (coming_soon etc.)
        if (def.comingSoon) return STATUS.COMING_SOON;
        if (def.id === 'iyzico') {
            return iyzicoConfig.enabled ? STATUS.ACTIVE : STATUS.INACTIVE;
        }
        return STATUS.INACTIVE;
    };

    const handleAction = (integration) => {
        if (integration.id === 'iyzico') {
            setIyzicoModalOpen(true);
        } else if (integration.id === 'paytr') {
            setPaytrModalOpen(true);
        } else if (integration.id === 'param') {
            setParamModalOpen(true);
        }
    };

    const handleSaveIntegration = async (key, config, label) => {
        setIsSaving(true);
        try {
            const updated = { ...savedIntegrations, [key]: config };
            await handleUpdateField('integrations', updated, `${label} entegrasyonu güncellendi`);
            if (key === 'iyzico') setIyzicoModalOpen(false);
            if (key === 'paytr') setPaytrModalOpen(false);
            if (key === 'param') setParamModalOpen(false);
        } catch (err) {
            console.error(`${key} save error:`, err);
            showAlert('Kaydetme sırasında hata oluştu.', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // Sort: active first, then inactive, then coming soon
    const sortedIntegrations = [...INTEGRATION_DEFS].sort((a, b) => {
        const order = { [STATUS.ACTIVE]: 0, [STATUS.INACTIVE]: 1, [STATUS.COMING_SOON]: 2 };
        return (order[getStatus(a)] ?? 2) - (order[getStatus(b)] ?? 2);
    });

    const activeCount = sortedIntegrations.filter(i => getStatus(i) === STATUS.ACTIVE).length;
    const comingSoonCount = sortedIntegrations.filter(i => getStatus(i) === STATUS.COMING_SOON).length;

    const activeList = sortedIntegrations.filter(i => getStatus(i) === STATUS.ACTIVE);
    const inactiveList = sortedIntegrations.filter(i => getStatus(i) === STATUS.INACTIVE);
    const comingSoonList = sortedIntegrations.filter(i => getStatus(i) === STATUS.COMING_SOON);

    return (
        <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>

            {/* Header Summary */}
            <div className="card glass" style={{
                padding: '2rem', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem',
                borderTop: '4px solid var(--primary)'
            }}>
                <div>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 800, margin: '0 0 0.5rem 0' }}>
                        Entegrasyonlar & Eklentiler
                    </h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 0, fontWeight: 500 }}>
                        İşletmenizi büyütmek için kullanabileceğiniz 3. parti araçlar ve entegrasyonlar.
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--primary)' }}>{activeCount}</div>
                        <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Aktif</div>
                    </div>
                    <div style={{ width: '1px', height: '32px', background: 'var(--border)' }} />
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--text-muted)' }}>{comingSoonCount}</div>
                        <div style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Yakında</div>
                    </div>
                </div>
            </div>

            {/* Active Integrations */}
            {activeList.length > 0 && (
                <div>
                    <h3 style={{ fontSize: '1rem', fontWeight: 800, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Check size={18} style={{ color: '#10b981' }} /> Aktif Entegrasyonlar
                    </h3>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))',
                        gap: '1.25rem'
                    }}>
                        {activeList.map(i => (
                            <IntegrationCard key={i.id} integration={i} status={getStatus(i)} onAction={handleAction} />
                        ))}
                    </div>
                </div>
            )}

            {/* Inactive (connectable) */}
            {inactiveList.length > 0 && (
                <div>
                    <h3 style={{ fontSize: '1rem', fontWeight: 800, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Plug size={18} style={{ color: 'var(--text-muted)' }} /> Bağlanabilir
                    </h3>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))',
                        gap: '1.25rem'
                    }}>
                        {inactiveList.map(i => (
                            <IntegrationCard key={i.id} integration={i} status={getStatus(i)} onAction={handleAction} />
                        ))}
                    </div>
                </div>
            )}

            {/* Coming Soon */}
            {comingSoonList.length > 0 && (
                <div>
                    <h3 style={{ fontSize: '1rem', fontWeight: 800, marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Clock size={18} style={{ color: '#f59e0b' }} /> Yakında Gelecek
                    </h3>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))',
                        gap: '1.25rem'
                    }}>
                        {comingSoonList.map(i => (
                            <IntegrationCard key={i.id} integration={i} status={getStatus(i)} onAction={handleAction} />
                        ))}
                    </div>
                </div>
            )}

            {/* iyzico Config Modal */}
            <IyzicoConfigModal
                isOpen={iyzicoModalOpen}
                onClose={() => setIyzicoModalOpen(false)}
                currentConfig={iyzicoConfig}
                onSave={(config) => handleSaveIntegration('iyzico', config, 'iyzico')}
                isSaving={isSaving}
            />

            {/* PayTR Config Modal */}
            <PaytrConfigModal
                isOpen={paytrModalOpen}
                onClose={() => setPaytrModalOpen(false)}
                currentConfig={paytrConfig}
                onSave={(config) => handleSaveIntegration('paytr', config, 'PayTR')}
                isSaving={isSaving}
            />

            {/* Param Config Modal */}
            <ParamConfigModal
                isOpen={paramModalOpen}
                onClose={() => setParamModalOpen(false)}
                currentConfig={paramConfig}
                onSave={(config) => handleSaveIntegration('param', config, 'Param')}
                isSaving={isSaving}
            />
        </div>
    );
};

export default Integrations;
